import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  FileText,
  List,
  RefreshCw,
  User,
  Users,
} from 'lucide-react';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import { useNr2jAutoRefresh } from '../../hooks/useNr2jAutoRefresh';
import apiService from '../../services/api';
import {
  NR2J_ACTIVITY_PAGE_SIZE,
  NR2J_ACTIVITY_PERIOD_PRESETS,
  NR2J_ACTIVITY_TYPE_FILTERS,
  activityFiltersAreActive,
  defaultActivityFilters,
  emptyActivitySummary,
  formatNr2jActivityTime,
  groupNr2jActivitiesByDay,
  nr2jActivityActionLabel,
  nr2jActivityActorName,
  nr2jActivityClientName,
  nr2jActivityDescription,
  nr2jActivityHref,
  nr2jActivityTone,
  periodRangeFromPreset,
} from '../../utils/nr2jActivityJournal.js';

function KpiCard({ title, value, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border p-4 text-left shadow-sm transition ${
        active
          ? 'border-indigo-400 bg-indigo-50 ring-1 ring-indigo-200'
          : 'border-gray-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/40'
      }`}
      data-testid={`nr2j-activity-kpi-${title}`}
    >
      <p className="text-sm text-gray-600">{title}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-900">{value ?? '—'}</p>
    </button>
  );
}

function ActionIcon({ row }) {
  const tone = nr2jActivityTone(row);
  const code = row?.action || row?.type || '';
  let Icon = List;
  if (code.startsWith('TASK_')) Icon = CheckSquare;
  else if (code.startsWith('QUOTE_') || /QUOTE_/.test(code)) Icon = FileText;
  else if (/SUBMITTED|APPROVED|REJECTED|CHANGES_REQUESTED/.test(code)) Icon = ClipboardCheck;
  else if (/PROSPECT_|CLIENT_|ORGANIZATION_|CONTACT_|ORG_/.test(code)) Icon = Building2;
  return (
    <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${tone.iconBg}`}>
      <Icon className="h-4 w-4" aria-hidden="true" />
    </span>
  );
}

export default function Nr2jActivity() {
  const navigate = useNavigate();
  const { toasts, toast, removeToast } = useToast();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(() => emptyActivitySummary());
  const [collaborators, setCollaborators] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextSkip, setNextSkip] = useState(null);
  const [filters, setFilters] = useState(() => defaultActivityFilters());
  const [openGroups, setOpenGroups] = useState({});
  const loadSeqRef = useRef(0);

  const load = useCallback(async (nextFilters, { append = false, skip = 0, silent = false } = {}) => {
    const seq = append ? loadSeqRef.current : (loadSeqRef.current += 1);
    if (append) setLoadingMore(true);
    else if (!silent) setLoading(true);
    try {
      const res = await apiService.listNr2jActivity({
        actorUserId: nextFilters.actorUserId || undefined,
        organizationId: nextFilters.organizationId || undefined,
        typeGroup: nextFilters.typeGroup || undefined,
        actionExact: nextFilters.actionExact || undefined,
        from: nextFilters.from || undefined,
        to: nextFilters.to || undefined,
        limit: NR2J_ACTIVITY_PAGE_SIZE,
        skip,
      });
      if (seq !== loadSeqRef.current) return;
      const batch = res?.activities || [];
      // Fil déjà filtré côté API (type / actionExact) — ne pas refiltrer localement
      // sinon un changement de filtre vide le fil avant l’arrivée de la réponse.
      setRows((prev) => (append ? [...prev, ...batch] : batch));
      setHasMore(Boolean(res?.page?.hasMore));
      setNextSkip(res?.page?.nextSkip ?? null);
      if (!append) {
        if (!silent) setOpenGroups({});
        // Synthèse KPI = périmètre (période / collab / client), indépendante du filtre type
        if (res?.summary) setSummary(res.summary);
        if (res?.filterOptions?.collaborators) setCollaborators(res.filterOptions.collaborators);
        if (res?.filterOptions?.clients) setClients(res.filterOptions.clients);
      }
    } catch (error) {
      if (seq !== loadSeqRef.current) return;
      if (!silent) toast.error(error?.message || 'Impossible de charger le journal d’activité');
      if (!append && !silent) {
        setRows([]);
        setSummary(emptyActivitySummary());
      }
      if (!silent) {
        setHasMore(false);
        setNextSkip(null);
      }
    } finally {
      // Toujours lever le spinner du load non-silencieux, même s’il est supersédé
      // par un refresh silencieux (sinon KPI « — » + « Chargement… » bloqués).
      if (!silent) setLoading(false);
      if (seq === loadSeqRef.current) {
        setLoadingMore(false);
      }
    }
  }, [toast]);

  useEffect(() => {
    load(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const silentReload = useCallback(() => {
    load(filtersRef.current, { silent: true });
  }, [load]);
  // runOnMount=false : le useEffect initial charge déjà — évite la course seq/loading.
  useNr2jAutoRefresh(silentReload, { enabled: true, intervalMs: 15000, runOnMount: false });

  const groups = useMemo(() => groupNr2jActivitiesByDay(rows), [rows]);
  const kpis = summary;
  const typeBreakdown = summary.typeBreakdown || [];
  const filtersActive = activityFiltersAreActive(filters);

  const isGroupOpen = (group) => {
    if (Object.prototype.hasOwnProperty.call(openGroups, group.key)) {
      return openGroups[group.key];
    }
    return group.defaultOpen;
  };

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const group = groups.find((g) => g.key === key);
      const current = Object.prototype.hasOwnProperty.call(prev, key)
        ? prev[key]
        : Boolean(group?.defaultOpen);
      return { ...prev, [key]: !current };
    });
  };

  const applyFilters = (patch, { reload = true } = {}) => {
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      if (reload) {
        queueMicrotask(() => load(next));
      }
      return next;
    });
  };

  const updateFilter = (key, value) => {
    applyFilters({ [key]: value });
  };

  const onPeriodPreset = (presetId) => {
    if (presetId === 'custom') {
      applyFilters({ periodPreset: 'custom' }, { reload: false });
      return;
    }
    const range = periodRangeFromPreset(presetId);
    applyFilters({
      ...range,
      kpiId: presetId === 'today' ? 'today' : '',
      actionExact: '',
      typeGroup: '',
    });
  };

  const toggleKpi = (kpiId) => {
    if (filters.kpiId === kpiId) {
      // Désélection : garde la période ; pour « Aujourd’hui » on reste sur aujourd’hui
      if (kpiId === 'today') {
        applyFilters({
          kpiId: '',
          actionExact: '',
          typeGroup: '',
          ...periodRangeFromPreset('today'),
        });
      } else {
        applyFilters({ kpiId: '', actionExact: '', typeGroup: '' });
      }
      return;
    }
    if (kpiId === 'today') {
      applyFilters({
        kpiId: 'today',
        actionExact: '',
        typeGroup: '',
        ...periodRangeFromPreset('today'),
      });
      return;
    }
    if (kpiId === 'quotesApproved') {
      applyFilters({ kpiId, actionExact: 'QUOTE_APPROVED', typeGroup: '' });
      return;
    }
    if (kpiId === 'tasksCompleted') {
      applyFilters({ kpiId, actionExact: 'TASK_COMPLETED', typeGroup: '' });
      return;
    }
    if (kpiId === 'changesRequested') {
      applyFilters({ kpiId, actionExact: 'CHANGES_REQUESTED', typeGroup: '' });
    }
  };

  const toggleTypeSidebar = (typeId) => {
    const next = filters.typeGroup === typeId ? '' : typeId;
    applyFilters({ typeGroup: next, actionExact: '', kpiId: '' });
  };

  const resetFilters = () => {
    const next = defaultActivityFilters();
    setFilters(next);
    load(next);
  };

  const openRow = (row) => {
    const href = nr2jActivityHref(row);
    if (!href) return;
    navigate(href);
  };

  return (
    <div className="min-w-0 space-y-6 overflow-x-hidden" data-testid="nr2j-activity-journal">
      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Journal d’activité</h1>
        <p className="text-sm text-gray-600">Suivi des actions des collaborateurs.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          title="Actions aujourd’hui"
          value={loading ? '—' : kpis.todayCount}
          active={filters.kpiId === 'today'}
          onClick={() => toggleKpi('today')}
        />
        <KpiCard
          title="Devis validés"
          value={loading ? '—' : kpis.quotesApproved}
          active={filters.kpiId === 'quotesApproved'}
          onClick={() => toggleKpi('quotesApproved')}
        />
        <KpiCard
          title="Tâches terminées"
          value={loading ? '—' : kpis.tasksCompleted}
          active={filters.kpiId === 'tasksCompleted'}
          onClick={() => toggleKpi('tasksCompleted')}
        />
        <KpiCard
          title="Demandes de modification"
          value={loading ? '—' : kpis.changesRequested}
          active={filters.kpiId === 'changesRequested'}
          onClick={() => toggleKpi('changesRequested')}
        />
      </div>

      <form
        className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
        onSubmit={(event) => {
          event.preventDefault();
          load(filters);
        }}
      >
        <label className="min-w-[150px] flex-1 text-sm text-gray-700">
          Collaborateur
          <select
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            value={filters.actorUserId}
            onChange={(e) => updateFilter('actorUserId', e.target.value)}
            data-testid="nr2j-activity-filter-collaborator"
          >
            <option value="">Tous les collaborateurs</option>
            {collaborators.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </label>
        <label className="min-w-[150px] flex-1 text-sm text-gray-700">
          Client
          <select
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            value={filters.organizationId}
            onChange={(e) => updateFilter('organizationId', e.target.value)}
            data-testid="nr2j-activity-filter-client"
          >
            <option value="">Tous les clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </label>
        <label className="min-w-[150px] flex-1 text-sm text-gray-700">
          Type d’action
          <select
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            value={filters.typeGroup}
            onChange={(e) => applyFilters({ typeGroup: e.target.value, actionExact: '', kpiId: '' })}
            data-testid="nr2j-activity-filter-type"
          >
            {NR2J_ACTIVITY_TYPE_FILTERS.map((f) => (
              <option key={f.id || 'all'} value={f.id}>{f.label}</option>
            ))}
          </select>
        </label>
        <label className="min-w-[150px] text-sm text-gray-700">
          Période
          <select
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            value={filters.periodPreset || 'custom'}
            onChange={(e) => onPeriodPreset(e.target.value)}
            data-testid="nr2j-activity-filter-period"
          >
            {NR2J_ACTIVITY_PERIOD_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </label>
        {filters.periodPreset === 'custom' ? (
          <>
            <label className="min-w-[130px] text-sm text-gray-700">
              Du
              <input
                type="date"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={filters.from}
                onChange={(e) => applyFilters({ from: e.target.value, periodPreset: 'custom' }, { reload: false })}
              />
            </label>
            <label className="min-w-[130px] text-sm text-gray-700">
              Au
              <input
                type="date"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={filters.to}
                onChange={(e) => applyFilters({ to: e.target.value, periodPreset: 'custom' }, { reload: false })}
              />
            </label>
          </>
        ) : null}
        <button
          type="submit"
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-700 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-800"
          data-testid="nr2j-activity-refresh"
        >
          <RefreshCw className="h-4 w-4" />
          Actualiser
        </button>
        {filtersActive ? (
          <button
            type="button"
            onClick={resetFilters}
            className="text-sm font-medium text-gray-500 hover:text-indigo-700"
            data-testid="nr2j-activity-reset"
          >
            Réinitialiser
          </button>
        ) : null}
      </form>

      <div className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(240px,280px)]">
        <section className="min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <List className="h-4 w-4 text-indigo-600" />
              <h2 className="text-sm font-semibold text-gray-900">Fil d’activité</h2>
            </div>
            <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
              {loading ? '…' : `${rows.length} action${rows.length > 1 ? 's' : ''} affichée${rows.length > 1 ? 's' : ''}`}
            </span>
          </div>

          {loading ? (
            <p className="p-6 text-sm text-gray-500">Chargement…</p>
          ) : rows.length === 0 ? (
            <p className="p-6 text-sm text-gray-500">Aucun événement pour ces filtres.</p>
          ) : (
            <div>
              {groups.map((group) => {
                const open = isGroupOpen(group);
                const Chevron = open ? ChevronDown : ChevronRight;
                return (
                  <div key={group.key} className="border-b border-gray-100 last:border-b-0">
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.key)}
                      className="flex w-full items-center gap-2 bg-gray-50 px-4 py-2.5 text-left hover:bg-gray-100"
                      aria-expanded={open}
                      data-testid="nr2j-activity-day-toggle"
                    >
                      <Chevron className="h-4 w-4 shrink-0 text-gray-500" />
                      <span className="min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide text-gray-600">
                        {group.label}
                      </span>
                      <span className="shrink-0 text-xs font-medium text-gray-500">
                        {group.count} action{group.count > 1 ? 's' : ''}
                      </span>
                    </button>
                    {open ? (
                      <ul>
                        {group.items.map((row) => {
                          const actor = nr2jActivityActorName(row);
                          const client = nr2jActivityClientName(row);
                          const href = nr2jActivityHref(row);
                          const clickable = Boolean(href);
                          return (
                            <li
                              key={row.id}
                              className={`flex items-start gap-3 px-4 py-3 ${
                                clickable ? 'cursor-pointer hover:bg-indigo-50/50' : ''
                              }`}
                              data-testid="nr2j-activity-row"
                              data-clickable={clickable ? '1' : '0'}
                              onClick={clickable ? () => openRow(row) : undefined}
                              onKeyDown={clickable ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openRow(row);
                                }
                              } : undefined}
                              role={clickable ? 'link' : undefined}
                              tabIndex={clickable ? 0 : undefined}
                            >
                              <span className="w-12 shrink-0 pt-2 text-xs tabular-nums text-gray-500">
                                {formatNr2jActivityTime(row.createdAt)}
                              </span>
                              <ActionIcon row={row} />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold text-gray-900">
                                  {nr2jActivityActionLabel(row)}
                                </p>
                                <p className="truncate text-sm text-gray-600">
                                  {nr2jActivityDescription(row)}
                                </p>
                                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                                  {actor ? (
                                    <span className="inline-flex min-w-0 items-center gap-1">
                                      <User className="h-3.5 w-3.5 shrink-0" />
                                      <span className="truncate">{actor}</span>
                                    </span>
                                  ) : null}
                                  {client ? (
                                    <span className="inline-flex min-w-0 items-center gap-1">
                                      <Building2 className="h-3.5 w-3.5 shrink-0" />
                                      <span className="truncate">{client}</span>
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                              {clickable ? (
                                <ChevronRight className="mt-2 h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </div>
                );
              })}

              {hasMore ? (
                <div className="border-t border-gray-100 p-4 text-center">
                  <button
                    type="button"
                    disabled={loadingMore}
                    onClick={() => load(filters, { append: true, skip: nextSkip || rows.length })}
                    className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                    data-testid="nr2j-activity-load-more"
                  >
                    {loadingMore ? 'Chargement…' : 'Afficher plus d’activités'}
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </section>

        <aside className="space-y-4">
          <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900">Actions rapides</h2>
            <ul className="mt-3 space-y-2">
              <li>
                <Link
                  to="/nr2j/a-valider"
                  className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-800"
                >
                  <ClipboardCheck className="h-4 w-4 text-indigo-600" />
                  Voir les validations
                </Link>
              </li>
              <li>
                <Link
                  to="/nr2j/suivi-commercial"
                  className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-800"
                >
                  <Users className="h-4 w-4 text-sky-600" />
                  Voir le suivi commercial
                </Link>
              </li>
              <li>
                <Link
                  to="/nr2j/taches"
                  className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-800"
                >
                  <CheckSquare className="h-4 w-4 text-indigo-600" />
                  Créer une tâche
                </Link>
              </li>
              <li>
                <Link
                  to="/nr2j/devis/nouveau"
                  className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-800"
                >
                  <FileText className="h-4 w-4 text-emerald-600" />
                  Nouveau devis
                </Link>
              </li>
            </ul>
          </section>

          {typeBreakdown.length > 0 ? (
            <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-900">Types d’actions</h2>
              <ul className="mt-3 space-y-1">
                {typeBreakdown.map((row) => {
                  const active = filters.typeGroup === row.id;
                  return (
                    <li key={row.id}>
                      <button
                        type="button"
                        onClick={() => toggleTypeSidebar(row.id)}
                        className={`flex w-full items-center justify-between rounded-lg px-2 py-2 text-sm ${
                          active
                            ? 'bg-indigo-50 font-medium text-indigo-800'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        data-testid={`nr2j-activity-type-${row.id}`}
                      >
                        <span className="inline-flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-full ${row.color}`} aria-hidden="true" />
                          {row.label}
                        </span>
                        <span className="tabular-nums text-gray-500">{row.count}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
